import { HttpClient, HttpHeaders } from '@angular/common/http'; // remove this and just use api service
import { Injectable, signal } from '@angular/core';
import { fallback } from 'assets/translations/en-gb_fallback';
import { environment } from 'environments/environment';
import moment from 'moment-timezone';
import { BehaviorSubject } from 'rxjs';
import { ConfigService } from 'app/config.service';
import { SupportedRegionType } from '../_models/supported-region.type';
import { ApiService } from './api.service';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root'
})
export class TranslationService {
	// Fall fall back
	t: any = fallback;

	langObj: Record<SupportedRegionType, string> = {
		'en-gb': 'English',
		'en-us': 'English',
		'nl-nl': 'Nederlands',
		'de-de': 'Deutsch',
		'de-at': 'Deutsch',
		'it-it': 'Italiano',
		'fr-fr': 'Français',
		'tr-tr': 'Türkçe',
		'ru-ru': 'Pусский',
		'pt-pt': 'Português',
		'pt-br': 'Português (BR)',
		'es-es': 'Español',
		'pl-pl': 'Polski',
		'zh-cn': '中文',
		'ar-sa': 'عربـي',
		'kk-kz': 'Қазақша',
		'uk-ua': 'українська мова',
		'sv-se': 'Svenska',
		'uz-uz': 'O\'zbekcha',
		'fi-fi': 'Suomi',
		'ko-kr': '한국어',
		'ja-jp': '日本語'
	};

	languages: any[] = [];
	selectedLang: SupportedRegionType = 'en-gb';
	hideLang = false;
	$translationsLoaded = signal(false);

	languageChangedObservable = new BehaviorSubject<boolean>(false);

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private userService: UserService,
		private apiService: ApiService,
	) {}

	getTranslation(): void {
		this.selectedLang = this.configService.getUserLang() as SupportedRegionType;
		const translationHeaders = new HttpHeaders({});
		const translationOptions = { headers: translationHeaders };
		const translationFallbackFile = `_grip_mobile_app_${this.selectedLang}.json`;

		let getUrl = `${environment.cloudFrontUrl}application-languages/`;

		// wait for config to set appId and fetch
		this.configService.isConfigSetState
			.subscribe(
				_res => {
					// config is set, fetch translations
					const translationFile = `_grip_mobile_app_applicationId_${this.configService.applicationId}_${this.selectedLang}.json`;
					// check if IE and use s3 origin rather than cloudfront (25-sec SSL handshake)
					if (window.navigator.userAgent.match(/(MSIE|Trident)/)) {
						getUrl = 'https://s3-eu-west-1.amazonaws.com/intros-api-public/application-languages/';
					}

					this.http.get(getUrl + translationFile, translationOptions).subscribe(
						data => {
							this.$translationsLoaded.set(true);
							for (const [key, value] of Object.entries(data)) {
								this.t[key] = value;
							}
							this.setLocale();
						},
						error => {
							this.http
								.get(getUrl + translationFallbackFile, translationOptions)
								.subscribe(
									data => {
										this.$translationsLoaded.set(true);
										for (const [key, value] of Object.entries(data)) {
											this.t[key] = value;
										}
									},
									error => {
										console.error(error);
									},
									() => {
										this.setLocale();
									}
								);
							console.error(error);
						}
					);
				},
				error => {
					// unable to fetch appId rollback to default translation file
					this.http.get(getUrl + translationFallbackFile, translationOptions).subscribe(
						data => {
							this.$translationsLoaded.set(true);
							for (const [key, value] of Object.entries(data)) {
								this.t[key] = value;
							}
						},
						error => {
							console.error(error);
						},
						() => {
							this.setLocale();
						}
					);
					console.error(error);
				}
			);
	}

	changeTranslation(lang: SupportedRegionType): void {
		if (!lang) return;
		const translationHeaders = new HttpHeaders({});
		const translationOptions = { headers: translationHeaders };
		const translationFileFallback = '_grip_mobile_app_' + lang.toLowerCase() + '.json';
		const translationFile = `_grip_mobile_app_applicationId_${this.configService.applicationId}_${this.selectedLang}.json`;

		let getUrl = `${environment.cloudFrontUrl}application-languages/`;
		// check if IE and use s3 origin rather than cloudfront (25-sec SSL handshake)
		if (window.navigator.userAgent.match(/(MSIE|Trident)/)) {
			getUrl =
				'https://s3-eu-west-1.amazonaws.com/intros-api-public/application-languages/';
		}

		this.http.get(getUrl + translationFile, translationOptions).subscribe(
			data => {
				for (const [key, value] of Object.entries(data)) {
					this.t[key] = value;
				}
			},
			error => {
				// fallback to default translation if error
				this.http.get(getUrl + translationFileFallback, translationOptions)
					.subscribe(
						response => {
							this.$translationsLoaded.set(true);
							for (const [key, value] of Object.entries(response)) {
								this.t[key] = value;
							}
							console.error(error);
						},
						error => {
							console.error(error);
						}
					);
			},
			() => {
				if (this.configService.mode === 'hosted_buyer') {
					this.getHostedBuyerTranslations().then(() => {
						this.setLocale();
					}, error => {
						console.error(error);
						this.setLocale();
					});
				} else {
					this.setLocale();
				}
			}
		);
	}

	getHostedBuyerTranslations() {
		return new Promise<void>(resolve => {
			const translationHeaders = new HttpHeaders({});
			const translationOptions = { headers: translationHeaders };
			const translationFile = `_grip_hosted_buyer_app_${this.selectedLang}.json`;

			let getUrl = `${environment.cloudFrontUrl}application-languages/`;
			// check if IE and use s3 origin rather than cloudfront (25-sec SSL handshake)
			if (window.navigator.userAgent.match(/(MSIE|Trident)/)) {
				getUrl =
					'https://s3-eu-west-1.amazonaws.com/intros-api-public/application-languages/';
			}

			this.http.get(getUrl + translationFile, translationOptions).subscribe(
				(data: any) => {
					for (const [key, value] of Object.entries(data)) {
						this.t[key] = value;
					}
				},
				error => {
					console.error(error);
				},
				() => {
					resolve();
				}
			);
		});
	}

	formatLanguages() {
		this.languages = [];
		this.configService.supported_locales.forEach(lang => {
			lang = lang.toLowerCase();
			if (lang.length === 2 && lang !== 'pt') {
				lang = `${lang}-${lang}`;
			}
			if (this.languages.indexOf(lang) === -1  && lang !== 'pt') {
				this.languages.push(lang);
			}
		});

		this.languages = this.languages
			.map(lang => {
				const language = {
					value: lang,
					name: this.langObj[lang]
				};
				return language;
			})
			.filter(lang => lang.name);

		if (this.languages.length < 2) {
			this.hideLang = true;
		}
	}

	onLangChange(): void {
		if (localStorage && typeof localStorage.setItem === 'function') {
			localStorage.setItem('gripLang', this.selectedLang);
		}
		this.userService.setDefaultHeaders(); // set new lang as accept-language header
		if (this.userService.user && this.userService.user.access_token) {
			this.userService.setAuthHeaders(this.userService.user.access_token); // set new lang as accept-language header
		}
		this.changeTranslation(this.selectedLang);
	}

	public setLocale(): void {
		// format moment for user language
		moment.locale(this.selectedLang);
		// update preferred language
		this.checkWhetherToUpdateUserPreferredLang(this.selectedLang);
		// alert rest of app lang has changed
		this.languageChangedObservable.next(true);
	}

	public checkWhetherToUpdateUserPreferredLang(userLang: SupportedRegionType): void {
		if (this.configService.$public() || !this.userService.user) {
			return;
		} else if (this.userService.user.location_code !== userLang) {
			// update language
			const body = { location_code: userLang };

			this.apiService
				.postMeGeneric(body)
				.subscribe();
		}
	}

	public agGridLocaleTextFunc = (key, defaultValue) => this.t?.['grid_' + key] || defaultValue;
}
